import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BannerComponent } from '../../../../../shared/components/banner/banner.component';
import { environment } from '../../../../../../environments';
import { AppointmentService } from '../../../../../core/services/appointment.service';
import { SwalService } from '../../../../../core/services/swal.service';
import { ModalService } from '../../../../../core/services';
import { MakePaymentComponent } from '../../../../../shared/components/modal/make-payment/make-payment.component';

interface Appointment {
  id: string;
  date: string;
  slot: string;
  patient: string;
  professional: string;
  location: string;
  status: 'confirmed' | 'pending' | 'cancelled' | 'completed';
  reason: string;
  startTime?: string;
  endTime?: string;
  notes?: string;
  structure?: {
    id: string;
    name: string;
  };
}

@Component({
  selector: 'app-appointments-liste',
  standalone: true,
  imports: [CommonModule, BannerComponent],
  templateUrl: './liste.component.html',
})
export class AppointmentsListeComponent implements OnInit {
  isDppTheme = environment.theme.default === 'dpp';
  links = [{ label: 'Rendez-vous', href: undefined }];

  // Pagination
  currentPage = 1;
  itemsPerPage = 10;
  totalItems = 0;
  Math = Math; // Make Math available in template

  // Données des rendez-vous
  appointments: Appointment[] = [];
  loading = false;

  constructor(
    private appointmentService: AppointmentService,
    private swalService: SwalService,
    private modalService: ModalService,
    
  ) {}

  ngOnInit(): void {
    this.loadAppointments();
  }

  loadAppointments(): void {
    this.loading = true;

    this.appointmentService
      .getAppointments({
        page: this.currentPage,
        limit: this.itemsPerPage,
      })
      .subscribe({
        next: (response: any) => {
          console.log(
            'AppointmentsListeComponent: Rendez-vous récupérés:',
            response
          );
          console.log(response);
          
          // Adapter la réponse de l'API au format attendu
          this.appointments = this.mapApiResponseToAppointments(response);
          this.totalItems = response.meta.totalItems ||  0;
          this.loading = false;
        },
        error: (error: any) => {
          console.error(
            'AppointmentsListeComponent: Erreur lors du chargement des rendez-vous:',
            error
          );
          this.swalService.error(
            'Erreur',
            'Impossible de charger les rendez-vous'
          );
          this.loading = false;
        },
      });
  }

  private mapApiResponseToAppointments(response: any): Appointment[] {
    const appointments = response.data || response || [];

    return appointments.map((appointment: any) => {
      // Construire le nom du patient à partir des données utilisateur
      let patientName = 'Patient';
      if (
        appointment.customFields?.patientName 
      ) {
        patientName =
          `${appointment.customFields?.patientName}`.trim();
      } else if (appointment.customFields?.patientName) {
        patientName = appointment?.customFields?.patientName;
      } else if (appointment.customFields?.passeport) {
        patientName = appointment.customFields.passeport;
      }

      // Construire le nom du professionnel
      let professionalName = 'Professionnel';
      if (appointment.customFields?.doctorName) {
        // Extraire le nom du docteur du champ "doctor"
        const doctorMatch = appointment.customFields?.doctorName.match(/Dr\.\s*([^"]+)/);
        if (doctorMatch) {
          professionalName = `Dr. ${doctorMatch[1].trim()}`;
        } else {
          professionalName = appointment.customFields?.doctorName;
        }
      } else if (appointment.professionalName) {
        professionalName = appointment.professionalName;
      } else if (appointment.externalId) {
        professionalName = appointment.externalId;
      }

      // Formater les créneaux horaires
      let startTime = appointment.startTime;
      let endTime = appointment.endTime;

      // Si les heures sont au format ISO, extraire seulement l'heure
      if (typeof startTime === 'string' && startTime.includes('T')) {
        startTime = startTime.split('T')[1]?.substring(0, 5) || startTime;
      }
      if (typeof endTime === 'string' && endTime.includes('T')) {
        endTime = endTime.split('T')[1]?.substring(0, 5) || endTime;
      }

      return {
        id: appointment.id || appointment._id,
        date: appointment.date,
        slot: `${startTime} - ${endTime}`,
        patient: patientName,
        professional: professionalName,
        location: appointment.customFields?.organizationName || 'Structure',
        status: this.mapStatus(appointment.status),
        reason:
          appointment.customFields?.reasonName ||
          appointment.customFields?.reason?.name ||
          'Motif non spécifié',
        startTime: startTime,
        endTime: endTime,
        notes: appointment.notes,
        structure: appointment.structure,
        acomptePrice: appointment.customFields?.acomptePrice || 0,
        acomptePaid: appointment.customFields?.acomptePaid || false,
      };
    });
  }

  private mapStatus(
    apiStatus: string
  ): 'confirmed' | 'pending' | 'cancelled' | 'completed' | 'rejected' {
    switch (apiStatus?.toLowerCase()) {
      case 'confirmed':
      case 'confirmé':
        return 'confirmed';
      case 'pending':
      case 'en_attente':
      case 'en attente':
        return 'pending';
      case 'cancelled':
      case 'annulé':
      case 'canceled':
        return 'cancelled';
      case 'completed':
      case 'terminé':
        return 'completed';
      case 'rejected':
      case 'refusé':
        return 'rejected';
      default:
        return 'pending';

    }
  }

  get paginatedAppointments(): Appointment[] {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    console.log(startIndex);
    
   

    let endIndex = startIndex + this.itemsPerPage;
     if (endIndex >= this.totalItems) {
      endIndex = this.totalItems;
    }
    // console.log(endIndex);
    // console.log(this.appointments.slice(startIndex, endIndex));
    // console.log(this.appointments);
    
    return this.appointments;
  }

  get totalPages(): number {
    return Math.ceil(this.totalItems / this.itemsPerPage);
  }

  get pages(): number[] {
    const pages: number[] = [];
    for (let i = 1; i <= this.totalPages; i++) {
      pages.push(i);
    }
    return pages;
  }

  changePage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadAppointments(); // Recharger les données pour la nouvelle page
    }
  }

  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'confirmed':
        return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800';
      case 'pending':
        return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800';
      case 'completed':
        return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800';
      default:
        return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800';
    }
  }

  getStatusText(status: string): string {
    switch (status) {
      case 'confirmed':
        return 'Confirmé';
      case 'pending':
        return 'En attente';
      case 'cancelled':
        return 'Annulé';
      case 'completed':
        return 'Terminé';
      case 'rejected':
        return 'Refusé';
      default:
        return status;
    }
  }

  canCancelAppointment(appointment: Appointment): boolean {
    // Vérifier si le rendez-vous est annulé ou terminé
   

    // Vérifier si le rendez-vous n'est pas encore passé
    const appointmentDate = new Date(appointment.date);
    const today = new Date();

    // Réinitialiser l'heure pour comparer seulement les dates
    today.setHours(0, 0, 0, 0);
    appointmentDate.setHours(0, 0, 0, 0);
    let check = appointmentDate >= today;
    // Le rendez-vous peut être annulé s'il est dans le futur ou aujourd'hui
     if (appointment.status !== 'cancelled' && appointment.status !== 'completed' && check) {
      return true;
    }
    else {
      return false;
    }
  }

  cancelAppointment(appointmentId: string): void {
    this.swalService
      .confirm(
        "Confirmer l'annulation",
        'Êtes-vous sûr de vouloir annuler ce rendez-vous ?'
      )
      .then((result) => {
        if (result.isConfirmed) {
          this.appointmentService.cancelReservation(appointmentId).subscribe({
            next: () => {
              this.swalService.success(
                'Rendez-vous annulé',
                'Le rendez-vous a été annulé avec succès'
              );
              this.loadAppointments();
            },
            error: (error) => {
              // this.swalService.error(
              //   'Erreur',
              //   "Impossible d'annuler le rendez-vous"
              // );
              this.loadAppointments();

            },
          });
        }
      });
  }
  payAppointment(appointmentId: any): void {
  
           this.modalService.open({
                component: MakePaymentComponent,
                width: '600px',
                closable: true,
                data: {
                  appointment: appointmentId,
                },
              });
     
  }   

}
